import { createFileRoute, Link } from '@tanstack/react-router';
import { usePrograms } from '@/data/programs';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/admin/programs/')({
  component: ProgramsIndex,
});

const titleCase = (value: string) =>
  value
    .split('_')
    .flatMap((part) => part.split('-'))
    .map((part, index) => (index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');

const primaryLinkClass =
  'inline-flex h-11 items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-5 font-medium text-[var(--color-ink-on-accent)] text-[var(--text-base)] shadow-[var(--shadow-sm)] transition-[background,color,box-shadow] duration-[var(--duration-fast)] ease-[var(--ease-out-quart)] hover:bg-[var(--color-accent-hover)] focus-visible:outline-none';

function ProgramsIndex() {
  const programs = usePrograms();

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Studio catalog
          </p>
          <h1 className="mt-2">Programs</h1>
        </div>
        <Link to="/admin/programs/new" className={primaryLinkClass}>
          New program
        </Link>
      </div>

      {programs.isLoading && <p aria-live="polite">Loading…</p>}

      {programs.data?.length === 0 && (
        <Empty
          title="No programs yet"
          description="Create offerings like camps, workshops, rentals, private lessons, and recurring classes."
          action={
            <Link to="/admin/programs/new" className={primaryLinkClass}>
              Create program
            </Link>
          }
        />
      )}

      {programs.data && programs.data.length > 0 && (
        <ul className="grid gap-3 md:grid-cols-2">
          {programs.data.map((program) => (
            <li key={program.id} className="surface-elevated">
              <Link
                to="/admin/programs/$id"
                params={{ id: program.id }}
                className="block space-y-4 p-5 hover:bg-[var(--color-surface-sunken)]"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">
                      {program.name}
                    </h2>
                    {program.description && (
                      <p className="mt-1 line-clamp-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                        {program.description}
                      </p>
                    )}
                  </div>
                  <Badge tone={program.active ? 'success' : 'neutral'}>
                    {program.active ? 'active' : 'inactive'}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge>{titleCase(program.program_type)}</Badge>
                  <Badge tone="accent">{titleCase(program.delivery_mode)}</Badge>
                  {program.default_capacity && <Badge>{program.default_capacity} seats</Badge>}
                </div>

                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  Default price:{' '}
                  <span className="font-medium text-[var(--color-ink)] tabular-nums">
                    {program.default_price_cents == null
                      ? 'Not set'
                      : `$${(program.default_price_cents / 100).toFixed(2)}`}
                  </span>
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
